import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { PaginationState } from '@tanstack/react-table'

export interface UseServerPaginationOptions {
  pageParam?: string
  limitParam?: string
  searchParam?: string
  defaultLimit?: number
  apiPageStart?: 0 | 1
  clearDefaults?: Record<string, any>
  resetOn?: any[]
}

export function useServerPagination(options: UseServerPaginationOptions = {}) {
  const {
    pageParam = 'page',
    limitParam = 'limit',
    searchParam = 'search',
    defaultLimit = 10,
    apiPageStart = 1,
    clearDefaults = {},
    resetOn,
  } = options

  const navigate = useNavigate()
  const searchParams = useSearch({ strict: false }) as Record<string, any>

  const rawPage = Number(searchParams[pageParam]) || 1
  const rawLimit = Number(searchParams[limitParam]) || defaultLimit
  const rawSearch = searchParams[searchParam] || ''

  const [inputValue, setInputValue] = useState(rawSearch)

  useEffect(() => {
    setInputValue(rawSearch)
  }, [rawSearch])

  const pagination: PaginationState = {
    pageIndex: rawPage - apiPageStart,
    pageSize: rawLimit,
  }

  const updateQueryParams = useCallback((newParams: Record<string, any>) => {
    navigate({
      search: (old: Record<string, any>) => ({ ...old, ...newParams }),
      replace: true,
    } as any)
  }, [navigate])

  const resetOnRef = useRef(resetOn)

  useEffect(() => {
    if (!resetOn) return
    const prev = resetOnRef.current
    const changed = !prev || prev.length !== resetOn.length || resetOn.some((val, i) => val !== prev[i])
    if (changed) {
      updateQueryParams({ [pageParam]: apiPageStart })
    }
    resetOnRef.current = resetOn
  }, [resetOn, updateQueryParams, pageParam, apiPageStart])

  const setPagination = useCallback((updater: PaginationState | ((prev: PaginationState) => PaginationState)) => {
    const newPagination = typeof updater === 'function' ? updater(pagination) : updater
    updateQueryParams({
      [pageParam]: newPagination.pageIndex + apiPageStart,
      [limitParam]: newPagination.pageSize,
    })
  }, [pagination, updateQueryParams, pageParam, limitParam, apiPageStart])

  const handleSearch = useCallback(() => {
    updateQueryParams({ [searchParam]: inputValue, [pageParam]: apiPageStart })
  }, [inputValue, updateQueryParams, searchParam, pageParam, apiPageStart])

  const handleClearFilters = useCallback(() => {
    setInputValue('')
    updateQueryParams({
      [searchParam]: '',
      [pageParam]: apiPageStart,
      [limitParam]: defaultLimit,
      ...clearDefaults,
    })
  }, [updateQueryParams, searchParam, pageParam, limitParam, defaultLimit, apiPageStart, clearDefaults])

  return {
    page: rawPage,
    limit: rawLimit,
    pagination,
    setPagination,
    search: rawSearch,
    inputValue,
    setInputValue,
    handleSearch,
    updateQueryParams,
    handleClearFilters,
  }
}
